import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, unwrap } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { useAuthStore } from '@/auth/authStore';
import { useIsPlatformUser } from '@/auth/useAuth';
import { NativeSelect } from '@/components/ui/input';

/** Platform roles pick the tenant scope (sent as X-Tenant-Id). Tenant users are pinned and see no selector. */
export function TenantSelector() {
  const { t } = useTranslation();
  const isPlatform = useIsPlatformUser();
  const pinnedTenant = useAuthStore((s) => s.user?.tenantId ?? null);
  const selected = useAuthStore((s) => s.selectedTenantId);
  const queryClient = useQueryClient();

  const tenants = useQuery({
    queryKey: queryKeys.tenants(),
    queryFn: async () => unwrap(await api.GET('/v1/tenants', { params: { query: { limit: 200 } } })),
    enabled: isPlatform && !pinnedTenant,
    meta: { silent: true },
  });

  if (!isPlatform || pinnedTenant) return null;

  return (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="hidden sm:inline">{t('tenant.label')}</span>
      <NativeSelect
        aria-label={t('tenant.select')}
        className="max-w-56"
        value={selected ?? ''}
        onChange={(e) => {
          useAuthStore.getState().selectTenant(e.target.value || null);
          // Tenant-scoped keys include the tenant id; invalidate so active views refetch in the new scope.
          void queryClient.invalidateQueries({ queryKey: ['tenant'] });
        }}
      >
        <option value="">{t('tenant.all')}</option>
        {tenants.data?.items.map((tn) => (
          <option key={tn.id} value={tn.id}>
            {tn.name}
          </option>
        ))}
      </NativeSelect>
    </label>
  );
}
